import React, { useState, useEffect } from 'react';
import JSZip from 'jszip';
import PrivateRoute from '../components/PrivateRoute';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../utils/hooks/use-auth';
import axios from "axios";
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SimCardDownloadOutlinedIcon from '@mui/icons-material/SimCardDownloadOutlined';
import TargetMonth from '../components/BillingInvoice/TargetMonth';
import SearchBoxInvoice from '../components/BillingInvoice/SearchBoxInvoice';
import { Spacer } from '../../utils/components/Spacer';
import InvoiceTable from '../components/BillingInvoice/InvoiceTable';
import BillingTab from '../components/Tab/BillingTab';
import { useInvoice } from "../hooks/useInvoiceSearch";
import { getCurrentUserToken } from '../../utils/functions';
import { StyledDiv, StyledTypograpy, StyledFooter, StyledToolbar } from '../styles/style';

const baseURL = process.env.REACT_APP_API_ENDPOINT;

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  color: '#0098CB',
  fontWeight: 'bold',
  margin: 10,
  padding: 10,
  '&:hover': {
    backgroundColor: "#FFFFFF"
  }
}));

interface fileInterface {
  filename: string;
  blob: Blob;
}

export function BillingInvoice() {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPdfDownload, setIsPdfDownload] = useState<boolean>(false);
  const [isCsvDownload, setIsCsvDownload] = useState<boolean>(false);
  const [isExcelDownload, setIsExcelDownload] = useState<boolean>(false);
  
  const {
    searchedData,
    getInvoiceData,
    page,
    invoiceNoList,
    setSearchConditionInvoiceNo,
    setSearchConditionFileType,
    resetData,
    pagenation,
    allCheck,
    singleCheck
  } = useInvoice();
  
  useEffect(() => {
    const getData = async() => {
      try {
        const _ = await getInvoiceData("", "");
        setIsLoading(false);
      }
      catch(error){
        console.log(error);
      }
    }
    getData();
  }, []);
  
  // =================== 請求書ボタン(開始) =====================
  const onLambdaPdfButtonClick = async () => {
    setIsPdfDownload(true);
    localStorage.removeItem('token');
    const token = await getCurrentUserToken();
    // ダウンロード対象のファイル名を抽出
    const downloadFiles: {downloadFilePaths: string, downloadFileNames: string}[] = [];
    const downloadFileNames: string[] = [];
    const downloadFilePaths: string[] = [];
    const targetYMs: string[] = [];
    searchedData.forEach((obj) => {
      if(obj.checked) {
        obj.contents.map((item) => {
          if(item.file_type === "請求書") {
            let downloadFile: {downloadFilePaths: string, downloadFileNames: string} = {
              "downloadFilePaths" : item.system_file_name,
              "downloadFileNames": item.download_file_name
            };
            downloadFilePaths.push(item.system_file_name);
            downloadFileNames.push(item.download_file_name);
            downloadFiles.push(downloadFile);
            targetYMs.push(obj.target_ym);
          }
        })
      }
      });
      
    if(downloadFiles.length === 0){
      window.alert("ダウンロード対象のファイルが存在しません");
      setIsPdfDownload(false);
    }
    // 単一のpdfファイルをダウンロード
    else if(downloadFiles.length === 1){
      axios.request({
          url: baseURL + "/billing-inquery-invoice-download",
          method: "post",
          headers: { 
            Accept: "application/pdf",
            Authorization: token,
          },
          data:{
            "downloadFiles": downloadFilePaths
          }
      })
      .then((response) => {
        // const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const blob: Blob = toBlobPdf(response.data, "application/pdf");
        const url = window.URL.createObjectURL(blob);
        // window.open(url, "_blank")  // pdfを別タブで表示
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadFileNames[0]);
        document.body.appendChild(link);
        link.click();
        setIsPdfDownload(false);
      })
      .catch((error) => {
        console.log(error);
        setIsPdfDownload(false);
      });
    }
    // zip形式のpdfファイル群をダウンロード
    else{
      const end = Math.max(...targetYMs.map(Number));
      const start = Math.min(...targetYMs.map(Number));
      
      
      if (downloadFiles.length <= 100) {
        axios.request({
          url: baseURL + "/billing-inquery-invoice-download",
          method: "post",
          headers: { 
            Accept: "application/zip",
            Authorization: token,
          },
          data:{
            "downloadFiles": downloadFiles
          }
        })
        .then((response) => {
          const blob: Blob = toBlobZip(response.data);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${auth.contractID}_${start}-${end}.zip`);
          document.body.appendChild(link);
          link.click();
          setIsPdfDownload(false);
        })
        .catch((error) => {
          console.log(error);
          setIsPdfDownload(false);
        });
      } else {
        // 1つのAPIが対処するファイル数をおよそ150件にする分割数の決定
        const segmentCount = downloadFiles.length / 100;
        
        // ダウンロードするファイル群をsegmentCountの数だけ等分する
        const chunkSize = Math.ceil(downloadFiles.length / segmentCount);
        const fileChunks = [];
        for (let i = 0; i < downloadFiles.length; i += chunkSize) {
          fileChunks.push(downloadFiles.slice(i, i + chunkSize));
        }
        
        // 新しいzipインスタンスを作成
        const finalZip = new JSZip();
        
        // 各チャンクごとにAPIリクエストを行い、結果を結合する
        try {
          const zipParts = await Promise.all(fileChunks.map(async (chunk, index) => {
            const response = await axios.request({
              url: baseURL + "/billing-inquery-invoice-download",
              method: "post",
              headers: { 
                Accept: "application/zip",
                Authorization: token,
              },
              data:{
                "downloadFiles": chunk
              }
            });
            
            // base64エンコードされたzipデータをBlobに変換
            const blob: Blob = toBlobZip(response.data);
      
            const zip = new JSZip();
            await zip.loadAsync(blob);
      
            const pdfFiles: Promise<fileInterface>[] = [];
            zip.forEach((relativePath, file) => {
              if (relativePath.endsWith('.pdf')) {
                pdfFiles.push(file.async('blob').then((blob) => ({
                  filename: relativePath,
                  blob: blob
                })));
              }
            });
            
            return await Promise.all(pdfFiles);
          }));
          
          zipParts.flat().forEach(({ filename, blob }) => {
            finalZip.file(filename, blob);
          });
        
          // 最終的なzipファイルを生成してダウンロード
          finalZip.generateAsync({ type: 'blob' }).then(function (content) {
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${auth.contractID}_${start}-${end}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
          
        } catch (error) {
          console.error(error);
        } finally {
          setIsPdfDownload(false);
        }
      }
      
    }
  };
  
  function toBlobPdf(base64: string, mime_ctype: string): Blob {
    // 日本語の文字化けに対処するためBOMを作成する。
    var bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    var bin = atob(base64.replace(/^.*,/, ''));
    var buffer = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    // Blobを作成
    var blob = new Blob([bom, buffer.buffer], {
        type: mime_ctype,
    });
    return blob;
  };
  // =================== 請求書ボタン(終了) =====================
  
  // =================== 明細一覧ボタン(開始) ===================
  const onLambdaCsvButtonClick = async () => {
    setIsCsvDownload(true);
    localStorage.removeItem('token');
    const token = await getCurrentUserToken();
    // ダウンロード対象のファイル名を抽出
    const downloadFiles: string[] = [];
    const downloadFileNames: string[] = [];
    const targetYMs: string[] = [];
    searchedData.forEach((obj) => {
      if(obj.checked) {
        obj.contents.map((item) => {
          if (item.file_type === "明細一覧") {
            downloadFiles.push(item.system_file_name)
            downloadFileNames.push(item.download_file_name);
            targetYMs.push(obj.target_ym);
          }
        })
      }
    });
    
    if(downloadFiles.length === 0){
      window.alert("ダウンロード対象のファイルが存在しません");
      setIsCsvDownload(false);
    }
    // 単一のcsvファイルをダウンロード
    else if(downloadFiles.length === 1){
      axios.request({
          url: baseURL + "/billing-inquery-invoice-download",
          method: "post",
          headers: { 
            Accept: "application/csv",
            Authorization: token,
          },
          data:{
            "downloadFiles": downloadFiles,
            "specification": ""
          }
      })
      .then((response) => {
        const blob: Blob = toBlobCsv(response.data, "application/csv");
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadFileNames[0]);
        document.body.appendChild(link);
        link.click();
        setIsCsvDownload(false);
      })
      .catch((error) => {
        console.log(error);
        setIsCsvDownload(false);
      });
    }
    // zip形式のcsvファイル群をダウンロード
    else{
      const end = Math.max(...targetYMs.map(Number));
      const start = Math.min(...targetYMs.map(Number));

      axios.request({
        url: baseURL + "/billing-inquery-invoice-download",
        method: "post",
        headers: { 
          Accept: "application/zip",
          Authorization: token,
        },
        data:{
          "downloadFiles": downloadFiles,
          "specification": "",
          "start": start,
          "end": end
        }
      })
      .then((response) => {
        const blob: Blob = toBlobZip(response.data);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${auth.contractID}_${start}-${end}.zip`);
        document.body.appendChild(link);
        link.click();
        setIsCsvDownload(false);
      })
      .catch((error) => {
        console.log(error);
        alert("ダウンロード可能なサイズ上限を超えたため、ダウンロード対象数を減らしてください")
        setIsCsvDownload(false);
      });
    }
  };
  
  function toBlobCsv(base64: string, mime_ctype: string): Blob {
    const byteArray = new Uint8Array(atob(base64).split('').map(function (char) {
      return char.charCodeAt(0);
    }));

    var bin = atob(base64);
    var buffer = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    // Blobを作成
    var blob = new Blob([byteArray], {
        type: mime_ctype,
    });
    return blob;
  };
  // =================== 明細一覧ボタン(終了) ===================
  
  // =================== 清算金額内訳ボタン(開始) ===================
  const onLambdaXlsxButtonClick = async () => {
    setIsExcelDownload(true);
    localStorage.removeItem('token');
    const token = await getCurrentUserToken();
    // ダウンロード対象ファイルを抽出
    const downloadFiles: {downloadFilePaths: string, downloadFileNames: string}[] = [];
    const downloadFileNames: string[] = [];
    const downloadFilePaths: string[] = [];
    const targetYMs: string[] = [];
    searchedData.forEach((obj) => {
      if(obj.checked) {
        obj.contents.map((item) => {
          if(item.file_type === "清算金額内訳") {
            let downloadFile: {downloadFilePaths: string, downloadFileNames: string} = {
              "downloadFilePaths" : item.system_file_name,
              "downloadFileNames": item.download_file_name
            };
            downloadFilePaths.push(item.system_file_name);
            downloadFileNames.push(item.download_file_name);
            downloadFiles.push(downloadFile);
            targetYMs.push(obj.target_ym);
          }
        })
      }
    });
    
    if(downloadFiles.length === 0){
      window.alert("ダウンロード対象のファイルが存在しません");
      setIsExcelDownload(false);
    }
    // 単一のxlsxファイルをダウンロード
    else if(downloadFiles.length === 1){
      axios.request({
          url: baseURL + "/billing-inquery-invoice-download",
          method: "post",
          headers: { 
            Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: token,
          },
          data:{
            "downloadFiles": downloadFilePaths
          }
      })
      .then((response) => {
        const blob: Blob = toBlobXlsx(response.data, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8");
        const url = window.URL.createObjectURL(blob);
        // window.open(url, "_blank")  // pdfを別タブで表示
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadFileNames[0]);
        document.body.appendChild(link);
        link.click();
        setIsExcelDownload(false);
      })
      .catch((error) => {
        console.log(error);
        setIsExcelDownload(false);
      });
    }
    // zip形式のxlsxファイル群をダウンロード
    else{
      const end = Math.max(...targetYMs.map(Number));
      const start = Math.min(...targetYMs.map(Number));
      
      if (downloadFiles.length <= 80) {
        axios.request({
            url: baseURL + "/billing-inquery-invoice-download",
            method: "post",
            headers: { 
              Accept: "application/zip",
              Authorization: token,
            },
            data:{
              "downloadFiles": downloadFiles
            }
        })
        .then((response) => {
          const blob: Blob = toBlobZip(response.data);
          const url = window.URL.createObjectURL(blob);
          // window.open(url, "_blank")  // pdfを別タブで表示
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${auth.contractID}_${start}-${end}.zip`);
          document.body.appendChild(link);
          link.click();
          setIsExcelDownload(false);
        })
        .catch((error) => {
          console.log(error);
          setIsExcelDownload(false);
        });
      } else {
        // 1つのAPIが対処するファイル数をおよそ150件にする分割数の決定
        const segmentCount = downloadFiles.length / 80;
        
        // ダウンロードするファイル群をsegmentCountの数だけ等分する
        const chunkSize = Math.ceil(downloadFiles.length / segmentCount);
        const fileChunks = [];
        for (let i = 0; i < downloadFiles.length; i += chunkSize) {
          fileChunks.push(downloadFiles.slice(i, i + chunkSize));
        }
        
        // 新しいzipインスタンスを作成
        const finalZip = new JSZip();
        
         // 各チャンクごとにAPIリクエストを行い、結果を結合する
        try {
          const zipParts = await Promise.all(fileChunks.map(async (chunk, index) => {
            const response = await axios.request({
              url: baseURL + "/billing-inquery-invoice-download",
              method: "post",
              headers: { 
                Accept: "application/zip",
                Authorization: token,
              },
              data:{
                "downloadFiles": chunk
              }
            })
            
            // base64エンコードされたzipデータをBlobに変換
            const blob: Blob = toBlobZip(response.data.data);
      
            const zip = new JSZip();
            await zip.loadAsync(blob);
      
            const xlsxFiles: Promise<fileInterface>[] = [];
            zip.forEach((relativePath, file) => {
              if (relativePath.endsWith('.xlsx')) {
                xlsxFiles.push(file.async('blob').then((blob) => ({
                  filename: relativePath,
                  blob: blob
                })));
              }
            });
            
            return await Promise.all(xlsxFiles);
          }));
          
          zipParts.flat().forEach(({ filename, blob }) => {
            finalZip.file(filename, blob);
          });
        
          // 最終的なzipファイルを生成してダウンロード
          finalZip.generateAsync({ type: 'blob' }).then(function (content) {
            const url = window.URL.createObjectURL(content);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${auth.contractID}_${start}-${end}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          });
          
        } catch (error) {
          console.error(error);
        } finally {
          setIsExcelDownload(false);
        }
      }
    }
  };
  
  function toBlobXlsx(base64: string, mime_ctype: string): Blob {
    const bin: string = atob(base64.replace(/^.*,/, ''));
    const buffer = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) {
        buffer[i] = bin.charCodeAt(i);
    }
    const data = new Uint8Array(buffer);
    const blob = new Blob([data], { type: mime_ctype});
    return blob;
  };
  // =================== 清算金額内訳ボタン(終了) ===================
  
  // base64エンコードされたzip形式のファイルをblob型に変換する
  function toBlobZip(base64: string): Blob {
    const bin = atob(base64.replace(/^.*,/, ''));
    const buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }
    const blob = new Blob([buffer.buffer], {
      type: 'application/zip'
    });
    return blob;
  };
  
  return (
    <PrivateRoute>
      <StyledDiv>
        <Spacer size={50} />
        <StyledTypograpy variant="h5">請求・明細</StyledTypograpy>
        <Spacer size={20} />
        <BillingTab initPage={"invoice"} />
        <Spacer size={20} />
        <TargetMonth searchedData={searchedData} getInvoiceData={getInvoiceData} setIsLoading={setIsLoading}/>
        <Spacer size={20} />
        {( 
          typeof(auth.requestPointID) !== "undefined" ? 
            <p style={{textAlign: 'center'}}>対象なし</p> :
          !isLoading ?
            <>
              <SearchBoxInvoice invoiceNoList={invoiceNoList} searchDataInvoiceNo={setSearchConditionInvoiceNo} searchDataFileType={setSearchConditionFileType} resetData={resetData} />
              <Spacer size={30} />
              <InvoiceTable
                allCheck={allCheck}
                singleCheck={singleCheck}
                searchedData={searchedData}
                page={page} 
                pagenation={pagenation} />
            </>
          :
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
        )}
      </StyledDiv>
      
      <StyledFooter sx={{ top: 'auto', bottom: 0 }}>
        <StyledToolbar>
          <Box sx={{ m: 1, position: 'relative' }}>
            <StyledButton
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={onLambdaPdfButtonClick}>PDFダウンロード</StyledButton>
            {isPdfDownload && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
          <Box sx={{ m: 1, position: 'relative' }}>
            <StyledButton
              startIcon={<SimCardDownloadOutlinedIcon />}
              onClick={onLambdaCsvButtonClick}>明細CSVダウンロード</StyledButton>
            {isCsvDownload && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
          <Box sx={{ m: 1, position: 'relative' }}>
            <StyledButton
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={onLambdaXlsxButtonClick}>清算金額Excelダウンロード</StyledButton>
            {isExcelDownload && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </StyledToolbar>
      </StyledFooter>
    </PrivateRoute>
  );
}